import React from "react";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  ItineraryItem,
  TripLodging,
  ServerFlightInfo,
  Participant,
  Currency,
  ActivityParticipant,
  TripTransport,
  type ActivityItem as ActivityItemType,
  FlightItem,
} from "@/types";
import Flight from "./Flight";
import Lodging from "./Lodging";
import TravelDay from "./TravelDay";
import Activity from "./Activity";
import useTripStore from "@/stores/tripBuilder/useTrip";
dayjs.extend(timezone);
dayjs.extend(utc);

interface ActivityItemProps {
  activity: ActivityItemType;
  tripId: string;
  nextActivity: ActivityItemType | null;
  onDelete: (id: string, name: string) => void;
  handleTimeChange: (time: Date, activityId: string) => void;
  activities: ActivityItemType[];
  locationName: string;
  locationTimeZone: string;
  handleOpenModal: (item: ItineraryItem | TripLodging) => void;
  selectedCurrency: Currency;
  isAdmin: boolean;
}

const ActivityItem: React.FC<ActivityItemProps> = ({
  activity,
  tripId,
  nextActivity,
  onDelete,
  handleTimeChange,
  activities,
  locationName,
  locationTimeZone,
  handleOpenModal,
  selectedCurrency,
  isAdmin,
}) => {
  const isLodging = activity.activityType === "accommodation";

  const isFlight = activity.activityType === "flight";

  const { selectedLocation, updateActivityItem, selectedDay } = useTripStore();
  const locationParticipants = selectedLocation?.participants || [];

  const isTravelDay = activity.activityType === "travel-day";

  const onUpdateActivityParticipants = async (
    activityId: string,
    participants: ActivityParticipant[],
    newStatus: "confirmed" | "pending" | "declined",
    onError?: () => void
  ): Promise<void> => {
    if (!selectedLocation?.id || !selectedDay) {
      console.error("Missing required location or day information");
      onError?.();
      return;
    }

    try {
      await updateActivityItem(selectedLocation.id, selectedDay, activityId, {
        participants,
      });
    } catch (error) {
      console.error("Failed to update activity participants:", error);
      onError?.();
    }
  };

  if (isFlight) {
    return (
      <Flight
        flight={activity as FlightItem}
        locationTimeZone={locationTimeZone}
        nextActivity={nextActivity}
        activities={activities}
        locationName={locationName}
      />
    );
  }

  if (isLodging) {
    return (
      <Lodging
        lodging={activity as TripLodging}
        locationTimeZone={locationTimeZone}
        nextActivity={nextActivity}
        activities={activities}
        locationName={locationName}
        selectedDay={selectedDay}
        isAdmin={isAdmin}
      />
    );
  }

  if (isTravelDay) {
    return <TravelDay activity={activity as ItineraryItem} />;
  }

  // Regular activity (experience)
  return (
    <Activity
      activity={activity as ItineraryItem}
      tripId={tripId}
      nextActivity={nextActivity}
      activities={activities}
      locationName={locationName}
      locationTimeZone={locationTimeZone}
      locationParticipants={locationParticipants}
      onUpdateActivityParticipants={onUpdateActivityParticipants}
      onDelete={onDelete}
      handleTimeChange={handleTimeChange}
      handleOpenModal={handleOpenModal}
      selectedCurrency={selectedCurrency}
      selectedDay={selectedDay}
      selectedLocationId={selectedLocation?.id || null}
      isAdmin={isAdmin}
    />
  );
};

export default ActivityItem;
